import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Box, Typography } from '@packages/shared';
import { ColorSelection } from './ColorSelection';
import { TilesSelection } from './TilesSelection';
import { ProductSelectionSelect } from './ProductSelectionSelect';
import type { SetSelectedVariation } from '../../types';
import type { SampleOrderCtAProps } from '../SampleOrderCtA';
import { SampleOrderCtA } from '../SampleOrderCtA';

const DimensionFragment = graphql(/* GraphQL */ `
  fragment DimensionFragment on ProductDimension {
    axisType
    displayName
    type
    ...TilesValuesFragment
    ...ColorTilesValuesFragment
    ...SelectValuesFragment
  }
`);

export type VariationSelectProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof DimensionFragment>;
  onChange: SetSelectedVariation;
  selectedVariation: string | null;
  sampleOrderData?: SampleOrderCtAProps | null;
};

/**
 * VariationSelect component decides which component-type to be rendered:
 * There are multiple ways, a product and its variations can be selected (e.g. Select, Tiles, ...).
 * Depending on configuration and backend-data, this component chooses a suiting
 * display-type.
 * */
export const VariationSelect = ({
  dimensionData,
  sampleOrderData = null,
  onChange,
  selectedVariation,
}: VariationSelectProps) => {
  const dimension = getFragmentData(DimensionFragment, dimensionData);
  let content = <ProductSelectionSelect dimensionData={dimension} onChange={onChange} />;

  if (dimension.axisType === 'COLOR' && dimension.type === 'TILES') {
    content = (
      <ColorSelection
        dimensionData={dimension}
        onChange={onChange}
        selectedVariation={selectedVariation}
      />
    );
  }

  if (dimension.axisType !== 'COLOR' && dimension.type === 'TILES') {
    content = (
      <TilesSelection
        dimensionData={dimension}
        onChange={onChange}
        selectedVariation={selectedVariation}
      />
    );
  }
  return (
    <Box>
      <Typography variant="body1">{dimension.displayName}</Typography>
      <Box sx={{ mt: 0.5 }}>{content}</Box>
      {sampleOrderData && dimension.axisType === 'COLOR' && (
        <SampleOrderCtA
          sx={{ mt: 1.5 }}
          akl={sampleOrderData.akl}
          sku={sampleOrderData.sku}
          sampleOrderType={sampleOrderData.sampleOrderType}
        />
      )}
    </Box>
  );
};
